import { find } from 'lodash';
import { ChatMessageTopic } from 'types/chatMessages/chatMessage';
import { EntitiesParams, HistorySearchParams } from 'types/getParams';

import { ChatMessageTopicsResp } from '@api/responseModels/chatMessages/chatMessageTopicsResponse';

import { ProductResp } from '@api/responseModels/product/productResponse';
import { ServiceResp } from '@api/responseModels/service/serviceResponse';
import { TaskResp } from '@api/responseModels/task/taskResponse';
import { WorksheetFieldsResp } from '@api/responseModels/worksheet/worksheetFieldsResponse';
import { WorksheetResp } from '@api/responseModels/worksheet/worksheetsResponse';
import { CRMAPIManager } from '@classes/crmApiManager';
import { OtherEntityType, SelectedTabItem } from '@components/common/searchTabs/common/types';
import { apiRequest } from '@utils/apiRequest';

import { TOPIC_CLASSNAMES } from '../components/orderComment/common/constants';

const OTHER_ENTITY_SINGULAR_TYPE: Record<OtherEntityType, string> = {
    services: 'service',
    products: 'product',
    tasks: 'task',
};

export interface SaveOtherEntityPayload {
    crm_id: number | undefined;
    name: string;
}

export const saveOtherEntity = async (
    entityType: OtherEntityType,
    payload: SaveOtherEntityPayload
): Promise<SelectedTabItem> => {
    const body = {
        ...payload,
        price: 0,
        quantity: 1,
        total: 0,
        is_other: true,
    };

    const requestByType: Record<OtherEntityType, () => Promise<any>> = {
        services: () =>
            CRMAPIManager.request<ServiceResp>(async (api) => api.saveService(body as any)),
        products: () =>
            CRMAPIManager.request<ProductResp>(async (api) => api.saveProduct(body as any)),
        tasks: () => CRMAPIManager.request<TaskResp>(async (api) => api.saveTask(body as any)),
    };

    const res = await requestByType[entityType]();
    const data = res.data.data;

    return {
        id: data.id,
        name: data.name,
        price: data.price,
        quantity: 1,
        type: OTHER_ENTITY_SINGULAR_TYPE[entityType],
        duration: data.duration,
    };
};

export const fetchWorksheets = async (crmID: number) => {
    const resp = await CRMAPIManager.request<WorksheetResp>(async (api) => {
        return await api.getWorksheets({
            crm_id: crmID,
            per_page: 500,
            page: 1,
        });
    });
    if (resp.errorMessages) throw resp.errorMessages;
    return resp.data.data;
};

export const fetchWorksheetFields = async (id: number, crmID: number) => {
    const resp = await CRMAPIManager.request<WorksheetFieldsResp>(async (api) => {
        return await api.getWorksheetFields(id, crmID);
    });
    if (resp.errorMessages) throw resp.errorMessages;
    return resp.data.data;
};

export const fetchEntitiesList = async (params: EntitiesParams) => {
    if (!params.term) return;
    const resp = await apiRequest(
        async (api) =>
            await api.search(params)
    );
    if (resp.errorMessages) throw resp.errorMessages;
    return resp.data.data;
};

export const fetchAddToSearchHistoryList = async (params: HistorySearchParams) => {
    if (!params.search_query) return;
    const resp = await apiRequest(
        async (api) =>
            await api.addToHistorySearch(params)
    );
    if (resp.errorMessages) throw resp.errorMessages;
    return resp.data.data;
};
export const fetchSearchHistoryList = async () => {
    const resp = await apiRequest(
        async (api) =>
            await api.getHistorySearch()
    );
    if (resp.errorMessages) throw resp.errorMessages;
    return resp.data.data;
};

/**
 * Тема чата заказа, заведённая под позицию (услугу, товар, задачу).
 *
 * Бэк создаёт такие темы сам при запросе тем по `order_id` и узнаёт их по ключу
 * `«класс модели»:id`. Писать и фильтровать по позиции надо через эту тему, а не
 * через саму позицию: темой её выбирают и в общем чате, а лента фильтруется по
 * точному совпадению пары тип + id.
 *
 * @returns `null`, если темы нет — например, позиция ещё не сохранена в заказе
 */
export const fetchEntityChatTopic = async (
    crmId: number,
    orderId: number,
    entityType: keyof typeof TOPIC_CLASSNAMES,
    entityId: number | string
): Promise<ChatMessageTopic | null> => {
    const resp = await CRMAPIManager.request<ChatMessageTopicsResp>(async (api) =>
        api.getChatMessageTopics(crmId, 1, orderId)
    );

    const key = `${TOPIC_CLASSNAMES[entityType]}:${entityId}`;

    return find(resp?.data?.data, (topic) => topic?.key === key) ?? null;
};
